/**
 * หน้า "วางแผนผลิตแบบ Manual" (`/planning/manual-plan`, `WO-P2-M5-010`, D-129) —
 * รายการแผน MPS ทุกเวอร์ชัน + สลับไปหน้าสร้างแผนใหม่ด้วยมือ
 *
 * แยกจากหน้า "แผนการผลิต" (`ProductionPlanScreen`, D-127) โดยตั้งใจ: หน้านั้นอ่าน
 * เทียบผลจริงอย่างเดียว ไม่มีปุ่มเขียนใด ๆ ส่วนหน้านี้คือที่เดียวที่ `POST
 * /v1/mps-versions` ถูกเรียกจากเว็บ (D-129 ข้อ 1) ชื่อเวอร์ชันในตารางข้างล่างเป็น
 * ลิงก์ไปหน้ารายละเอียด/ขับสถานะ (`WO-P2-M5-011`) ส่วนลิงก์ "ดูเทียบผลิตจริง" ยังชี้
 * ไปหน้าแผนการผลิตเดิมเหมือนก่อน
 *
 * sub-page (`?view=create`) เป็น query param ไม่ใช่ nested route แบบเดียวกับ
 * `ProductMasterScreen` (`WO-P2-M11-004`) — `App.tsx` ประกาศ route ของทุกโมดูลจาก
 * `DESKTOP_MODULE_ROUTES` เส้นละหนึ่ง path ตรงตัว
 *
 * **`?view=detail&mps=<mps_id>` (`WO-P2-M5-011`, D-129 ข้อ 2-3)** — รายละเอียดแผน
 * + ขับสถานะ ชื่อเวอร์ชันในตารางข้างล่างเป็นลิงก์ไปที่นี่ (`detailSearch`) ต่อท้าย
 * query เดิมแบบเดียวกับที่ `CustomerScreen`/`detailSearch` ทำกับลูกค้า เพื่อให้ปุ่ม
 * "กลับไปรายการ" ของหน้ารายละเอียดคืนตัวกรองเดิมได้ (`listSearch`) โดยไม่ต้องมีกลไก
 * ที่สองมาเดา URL ปุ่ม "สร้างแผนใหม่" (เดิม) ไม่ผ่าน `createSearch` เพราะไม่มี `from`
 * ให้ต่อ — พฤติกรรมเดิมของ `WO-P2-M5-010` ไม่เปลี่ยน
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import type { Breadcrumb } from '../HeaderBar';
import { useTrailingCrumbs } from '../desktopContext';
import { ManualPlanCreateView } from './ManualPlanCreateView';
import { ManualPlanDetailView } from './ManualPlanDetailView';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { fetchMpsVersions, ruleFromMpsFailure, type MpsFailure } from '../../lib/mps/client';
import { MPS_VERSION_STATUSES, PLANNING_MODULE, type MpsVersionSummary } from '../../lib/mps/types';
import { mpsVersionStatusLabel, mpsVersionStatusTone } from '../../lib/mps/mpsView';

export interface ManualPlanScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

const PAGE_SIZE = 20;
const CREATE_VIEW_SEARCH = '?view=create';

/** query ของหน้ารายละเอียด — ต่อจากพารามิเตอร์ที่มีอยู่ ไม่เขียนทับทั้งเส้น (เหตุผล
 * เดียวกับ `detailSearch` ของ `CustomerScreen`) */
function detailSearch(params: URLSearchParams, mpsId: string): string {
  const next = new URLSearchParams(params);
  next.set('view', 'detail');
  next.set('mps', mpsId);
  next.delete('from');
  return `?${next.toString()}`;
}

/** query ของหน้าสร้างเวอร์ชันใหม่ "จากแผนนี้" — ไม่ต่อพารามิเตอร์ของรายการ เพราะเป็น
 * การเริ่มฟอร์มใหม่ ไม่ใช่การกลับไปที่หน้ารายการ (D-129 ข้อ 2) */
function createFromSearch(mpsId: string): string {
  const params = new URLSearchParams();
  params.set('view', 'create');
  params.set('from', mpsId);
  return `?${params.toString()}`;
}

/** query ของรายการ — ตัด `view`/`mps`/`from` ออก เหลือตัวกรองเดิม (`listSearch` ของ
 * `CustomerScreen`) */
function listSearch(params: URLSearchParams): string {
  const next = new URLSearchParams(params);
  next.delete('view');
  next.delete('mps');
  next.delete('from');
  const serialized = next.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly MpsVersionSummary[]; readonly nextCursor: string | null }
  | MpsFailure;

/** กองของ cursor ที่ผูกกับตัวกรองชุดที่มันเกิดมา — แนวเดียวกับ `ProductionPlanScreen` (D-123) */
interface ScopedPaging {
  readonly key: string;
  readonly cursors: readonly string[];
}

const NO_CURSORS: readonly string[] = [];

function failureHeading(failure: MpsFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้';
    case 'forbidden':
      return 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้';
    case 'not-found':
      return 'ไม่พบข้อมูลนี้';
    case 'conflict':
      return 'คำขอนี้ชนกับสถานะปัจจุบัน';
    case 'rule-violation':
      return 'คำขอนี้ติดกฎทางธุรกิจ';
    case 'bad-request':
      return 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`;
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้';
    case 'invalid-response':
      return 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ';
    case 'cancelled':
      return 'ยกเลิกการเรียกข้อมูลแล้ว';
  }
}

function failureText(failure: MpsFailure): string {
  const rule = ruleFromMpsFailure(failure);
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)';
    case 'forbidden':
      return `คำขอนี้ต้องการโมดูล ${PLANNING_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่`;
    case 'not-found':
      return 'ไม่พบข้อมูลนี้ หรืออยู่นอกขอบเขตของคุณ';
    case 'conflict':
      return 'เซิร์ฟเวอร์ตอบ 409';
    case 'rule-violation':
      return rule === null ? 'เซิร์ฟเวอร์ตอบ 422 พร้อมกฎทางธุรกิจ' : `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${rule}`;
    case 'bad-request':
      return 'ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ';
    case 'server-error':
      return 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ';
    case 'network-error':
      return failure.detail;
    case 'invalid-response':
      return 'หน้าจอปฏิเสธที่จะแสดงแถวที่อ่านได้ไม่ครบ';
    case 'cancelled':
      return 'คำขอถูกยกเลิกก่อนได้รับคำตอบ';
  }
}

function FailureNotice({
  failure,
  onRetry,
}: {
  readonly failure: MpsFailure;
  readonly onRetry: () => void;
}) {
  return (
    <div className="cap-notice" data-testid="manual-plan-failure" data-failure={failure.status} role="status">
      <StatusBadge tone="danger">ทำรายการไม่สำเร็จ</StatusBadge>
      <h3 className="cap-notice__title">{failureHeading(failure)}</h3>
      <p className="cap-notice__text">{failureText(failure)}</p>
      <div className="cap-notice__actions">
        <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
          ลองอีกครั้ง
        </button>
      </div>
    </div>
  );
}

function CreateNotOfferedNotice() {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="manual-plan-create-unavailable" role="status">
        <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
        <h1 className="cap-notice__title">สิทธิ์ชุดนี้สร้างแผนผลิตไม่ได้</h1>
        <p className="cap-notice__text">
          สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code> ของโมดูล{' '}
          <code className="cap-table__code">{PLANNING_MODULE}</code> หน้าจอนี้จึงไม่เปิดฟอร์มให้กรอก
        </p>
        <p className="cap-notice__note">
          การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนบันทึก — ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ
        </p>
      </div>
    </div>
  );
}

export function ManualPlanScreen({ permissions, apiBaseUrl, fetchImpl }: ManualPlanScreenProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const setTrailingCrumbs = useTrailingCrumbs();

  const isCreateView = searchParams.get('view') === 'create';
  const isDetailView = searchParams.get('view') === 'detail';
  const canCreate = can(permissions, PLANNING_MODULE, 'CREATE');
  const status = searchParams.get('status') ?? '';

  useEffect(() => {
    const crumbs: readonly Breadcrumb[] = isCreateView
      ? [{ label: 'สร้างแผนใหม่' }]
      : isDetailView
        ? [{ label: 'รายละเอียดแผน' }]
        : [];
    setTrailingCrumbs(crumbs);
  }, [isCreateView, isDetailView, setTrailingCrumbs]);

  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const filterKey = status;
  const [paging, setPaging] = useState<ScopedPaging>({ key: filterKey, cursors: NO_CURSORS });
  const cursors = paging.key === filterKey ? paging.cursors : NO_CURSORS;
  const cursor = cursors.length === 0 ? undefined : cursors[cursors.length - 1];

  useEffect(() => {
    if (isCreateView || isDetailView) {
      return;
    }
    let active = true;
    const controller = new AbortController();
    setList((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchMpsVersions(
      {
        ...(status === '' ? {} : { status }),
        limit: PAGE_SIZE,
        ...(cursor === undefined ? {} : { cursor }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setList(result);
        return;
      }
      setList({ status: 'ready', items: result.page.items, nextCursor: result.page.next_cursor });
    });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, fetchImpl, isCreateView, isDetailView, status, cursor, reloadToken]);

  if (isCreateView) {
    if (!canCreate) {
      return <CreateNotOfferedNotice />;
    }
    return (
      <ManualPlanCreateView
        permissions={permissions}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        copyFromMpsId={searchParams.get('from')}
      />
    );
  }

  if (isDetailView) {
    const mpsId = searchParams.get('mps') ?? '';
    return (
      <ManualPlanDetailView
        mpsId={mpsId}
        permissions={permissions}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        backSearch={listSearch(searchParams)}
        createFromSearch={createFromSearch(mpsId)}
        onPlanChanged={() => {
          setReloadToken((token) => token + 1);
        }}
      />
    );
  }

  const applyFilter = (change: (params: URLSearchParams) => void) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        change(next);
        return next;
      },
      { replace: true },
    );
  };

  const firstRowNumber = cursors.length * PAGE_SIZE + 1;
  const items = list.status === 'ready' ? list.items : [];
  const lastRowNumber = firstRowNumber + items.length - 1;
  const rowRangeLabel = items.length === 0 ? '' : `แถว ${firstRowNumber}–${lastRowNumber}`;

  return (
    <div className="cap-page" data-testid="manual-plan">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            แผนการผลิตแบบ manual{' '}
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text="รายการแผน MPS ทุกเวอร์ชัน — สร้างแผนใหม่ด้วยมือได้ที่นี่"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          {canCreate ? (
            <Link
              className="cap-button cap-button--primary"
              data-testid="manual-plan-create-open"
              to={{ pathname, search: CREATE_VIEW_SEARCH }}
            >
              สร้างแผนใหม่
            </Link>
          ) : null}
        </div>
      </div>

      <div className="cap-filters" data-testid="manual-plan-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            data-testid="manual-plan-status-filter"
            value={status}
            onChange={(event) => {
              const value = event.target.value;
              applyFilter((params) => {
                if (value === '') {
                  params.delete('status');
                } else {
                  params.set('status', value);
                }
              });
            }}
          >
            <option value="">ทุกสถานะ</option>
            {MPS_VERSION_STATUSES.map((value) => (
              <option key={value} value={value}>
                {mpsVersionStatusLabel(value)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {list.status === 'loading' ? (
        <div className="cap-notice" data-testid="manual-plan-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการแผน…</p>
        </div>
      ) : null}

      {list.status !== 'loading' && list.status !== 'ready' ? (
        <FailureNotice
          failure={list}
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {list.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="manual-plan-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีแผน</StatusBadge>
          <p className="cap-notice__text">เซิร์ฟเวอร์ตอบสำเร็จแต่ยังไม่มีแผน MPS ที่ตรงกับตัวกรองนี้</p>
        </div>
      ) : null}

      {list.status === 'ready' && items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="รายการแผน MPS">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="manual-plan-table">
              <thead>
                <tr>
                  <th scope="col">ชื่อเวอร์ชัน</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">ช่วงวางแผน</th>
                  <th scope="col">ตรึงถึง</th>
                  <th scope="col">หมายเหตุ</th>
                  <th scope="col">การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {items.map((version) => (
                  <tr key={version.mps_id} data-testid={`manual-plan-row-${version.mps_id}`}>
                    <td>
                      <Link
                        className="cap-table__link"
                        data-testid={`manual-plan-detail-open-${version.mps_id}`}
                        to={{ pathname, search: detailSearch(searchParams, version.mps_id) }}
                      >
                        {version.version_label}
                      </Link>
                    </td>
                    <td>
                      <code className="cap-table__code">{version.plant_code}</code>
                    </td>
                    <td>
                      <StatusBadge tone={mpsVersionStatusTone(version.status)}>
                        {mpsVersionStatusLabel(version.status)}
                      </StatusBadge>
                    </td>
                    <td>
                      {version.horizon_start}–{version.horizon_end}
                    </td>
                    <td>{version.frozen_until ?? '—'}</td>
                    <td>{version.note ?? '—'}</td>
                    <td>
                      <Link
                        className="cap-button cap-button--ghost"
                        data-testid={`manual-plan-compare-${version.mps_id}`}
                        to={`/planning/production-plan?mps=${version.mps_id}`}
                      >
                        ดูเทียบผลิตจริง
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cap-card__foot cap-pager" data-testid="manual-plan-pager">
            <span className="cap-pager__count">{rowRangeLabel}</span>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="manual-plan-page-prev"
              disabled={cursors.length === 0}
              onClick={() => {
                setPaging({ key: filterKey, cursors: cursors.slice(0, -1) });
              }}
            >
              ก่อนหน้า
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="manual-plan-page-next"
              disabled={list.status !== 'ready' || list.nextCursor === null}
              onClick={() => {
                if (list.status === 'ready' && list.nextCursor !== null) {
                  setPaging({ key: filterKey, cursors: [...cursors, list.nextCursor] });
                }
              }}
            >
              ถัดไป
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
